import { Capacitor, CapacitorHttp } from '@capacitor/core'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import {
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  type User,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { auth, googleProvider, appleProvider, functions } from '../lib/firebase'
import { initializePlatformServices } from '../lib/platform'
import {
  adoptGuestWorkspace,
  guestSessionsSupported,
  isGuestUser,
  registerGuestDevice,
  startNativeGuestSession,
} from '../lib/guestSession'
import { runNativeAuthCall } from '../utils/nativeFirebaseAuthQueue'
import {
  getFunctionsBaseUrl,
  signOutUser,
  syncNativeAuthToWeb,
} from '../utils/nativeAuth'

interface AuthState {
  user: User | null
  loading: boolean
  /** True while the session is an anonymous guest that has no account yet. */
  isGuest: boolean
  /** Makes sure a guest session exists where the platform supports one. */
  ensureGuestSession: () => Promise<User | null>
  signInGoogle: () => Promise<void>
  signInApple: () => Promise<void>
  requestEmailCode: (email: string) => Promise<void>
  verifyEmailCode: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

const callRecordLegalAcceptance = httpsCallable<
  { locale: string },
  {
    ok: boolean
    termsVersion: string
    privacyNoticeVersion: string
    cancelledDeletionProtocol: 'account' | 'data' | null
  }
>(functions, 'recordLegalAcceptance', { limitedUseAppCheckTokens: true })

function isUnauthenticatedError(cause: unknown): boolean {
  const code = String((cause as { code?: string })?.code ?? '').toLowerCase()
  const message = cause instanceof Error ? cause.message.toLowerCase() : ''
  return code.includes('unauthenticated') || message.includes('unauthenticated')
}

function waitForAuthPropagation(durationMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, durationMs))
}

async function recordLegalAcceptance(signedInUser: User) {
  try {
    await initializePlatformServices()
  } catch (error) {
    console.warn(
      '[VOXNELA_AUTH] Platform servisleri yasal kabul kaydından önce hazırlanamadı.',
      error,
    )
  }

  let lastError: unknown
  try {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        await signedInUser.getIdToken(true)
        await callRecordLegalAcceptance({
          locale: document.documentElement.lang || navigator.language || 'tr',
        })
        return
      } catch (error) {
        lastError = error
        const sameAuthenticatedUser = auth.currentUser?.uid === signedInUser.uid
        if (!sameAuthenticatedUser || !isUnauthenticatedError(error)) throw error
        await waitForAuthPropagation(180)
      }
    }
  } catch (error) {
    lastError = error
  }

  console.warn('[VOXNELA_AUTH] Oturum açıldı; yasal kabul kaydı tamamlanamadı.', lastError)
}

async function postOtpHttp<T = Record<string, unknown>>(
  endpoint: string,
  payload: Record<string, unknown>,
): Promise<T> {
  const url = `${getFunctionsBaseUrl()}/${endpoint}`
  if (Capacitor.isNativePlatform()) {
    const res = await CapacitorHttp.request({
      url,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: payload,
      connectTimeout: 15000,
      readTimeout: 15000,
    })
    if (res.status !== 200) {
      const errMessage = res.data?.error || `Request failed (${res.status})`
      throw new Error(errMessage)
    }
    return res.data as T
  } else {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data?.error || `Request failed (${res.status})`)
    }
    return data as T
  }
}

async function startGuestSession(): Promise<User> {
  try {
    return await startNativeGuestSession()
  } catch (nativeError) {
    // A Web SDK guest still lets the person continue on this launch; the native
    // Keychain session will be retried on the next start.
    console.warn('[VOXNELA_AUTH] Native guest session failed; using a web guest session.', nativeError)
    return (await signInAnonymously(auth)).user
  }
}

async function currentGuestIdToken(): Promise<string | null> {
  const current = auth.currentUser
  if (!current || !(await isGuestUser(current).catch(() => false))) return null
  return current.getIdToken().catch(() => null)
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(auth.currentUser)
  const [loading, setLoading] = useState(true)
  const [isGuest, setIsGuest] = useState(false)
  const guestSessionRef = useRef<Promise<User | null> | null>(null)
  const registeredGuestRef = useRef<string | null>(null)

  const ensureGuestSession = async (): Promise<User | null> => {
    if (auth.currentUser) return auth.currentUser
    if (!guestSessionsSupported()) return null
    guestSessionRef.current ??= startGuestSession()
      .then((guest) => {
        setUser(guest)
        return guest
      })
      .catch((error) => {
        console.warn('[VOXNELA_AUTH] Guest session could not be started.', error)
        return null
      })
      .finally(() => {
        guestSessionRef.current = null
      })
    return guestSessionRef.current
  }

  // Signing in from a guest session keeps what the guest created on this device.
  const withGuestHandoff = async (signIn: () => Promise<void>) => {
    const guestIdToken = await currentGuestIdToken()
    await signIn()
    const signedInUser = auth.currentUser
    if (!guestIdToken || !signedInUser) return
    try {
      await adoptGuestWorkspace(guestIdToken, signedInUser.uid)
    } catch (error) {
      console.warn('[VOXNELA_AUTH] Misafir görselleri hesaba taşınamadı.', error)
    }
    // Email-code sign-in replaces only the Web SDK session; drop the native
    // guest so it is not restored after the merged guest has been disabled.
    if (Capacitor.isNativePlatform()) {
      const nativeUser = await runNativeAuthCall('getCurrentUser', () =>
        FirebaseAuthentication.getCurrentUser(),
      ).catch(() => null)
      if (nativeUser?.user?.isAnonymous) {
        await runNativeAuthCall('signOut', () => FirebaseAuthentication.signOut()).catch(() => undefined)
      }
    }
  }

  useEffect(() => {
    let active = true
    if (!user) {
      queueMicrotask(() => { if (active) setIsGuest(false) })
      return () => { active = false }
    }
    void isGuestUser(user).catch(() => false).then((guest) => {
      if (active) setIsGuest(guest)
      if (!guest || registeredGuestRef.current === user.uid) return
      registeredGuestRef.current = user.uid
      void registerGuestDevice().catch((error) => {
        registeredGuestRef.current = null
        console.warn('[VOXNELA_AUTH] Guest device could not be registered.', error)
      })
    })
    return () => { active = false }
  }, [user])

  useEffect(() => {
    let active = true
    let restoringNativeSession = Capacitor.isNativePlatform()

    const unsubscribe = onAuthStateChanged(auth, (current) => {
      if (!active) return
      setUser(current)
      if (!restoringNativeSession) setLoading(false)
    })

    // On iOS native platform, check if there is an existing native session to sync to web
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        try {
          const nativeUser = await runNativeAuthCall('getCurrentUser', () =>
            FirebaseAuthentication.getCurrentUser(),
          )
          if (nativeUser?.user && !auth.currentUser) {
            console.log('[VOXNELA_AUTH] Restoring native session to Web SDK...')
            const restoredUser = await syncNativeAuthToWeb(nativeUser)
            if (active) setUser(restoredUser)
          }
        } catch (err) {
          console.log('[VOXNELA_AUTH] No native session to restore or sync skipped:', err)
        }
        if (active && !auth.currentUser) await ensureGuestSession()
        restoringNativeSession = false
        if (active) setLoading(false)
      })()
    }

    return () => {
      active = false
      unsubscribe()
    }
  }, [])

  const signInGoogleSession = async () => {
    if (!Capacitor.isNativePlatform()) {
      const credential = await signInWithPopup(auth, googleProvider)
      void recordLegalAcceptance(credential.user)
      return
    }

    try {
      console.log('[VOXNELA_AUTH] Starting native Google Sign-In...')
      const nativeResult = await runNativeAuthCall('signInWithGoogle', () =>
        FirebaseAuthentication.signInWithGoogle({
          skipNativeAuth: false,
          useCredentialManager: Capacitor.getPlatform() === 'android',
        }),
      )

      let signedInUser: User
      try {
        signedInUser = await syncNativeAuthToWeb(nativeResult)
      } catch (syncError) {
        console.warn('[VOXNELA_AUTH] Native session sync failed; trying Google credential.', syncError)
        const idToken = nativeResult?.credential?.idToken
        const accessToken = nativeResult?.credential?.accessToken
        if (!idToken && !accessToken) throw syncError
        const credential = GoogleAuthProvider.credential(
          idToken || undefined,
          accessToken || undefined,
        )
        signedInUser = (await signInWithCredential(auth, credential)).user
      }

      void recordLegalAcceptance(signedInUser)
    } catch (error: unknown) {
      console.error('[VOXNELA_AUTH] Native Google login error:', error)
      throw error
    }
  }

  const signInAppleSession = async () => {
    if (!Capacitor.isNativePlatform()) {
      const credential = await signInWithPopup(auth, appleProvider)
      void recordLegalAcceptance(credential.user)
      return
    }

    try {
      console.log('[VOXNELA_AUTH] Starting native Apple Sign-In...')
      const nativeResult = await runNativeAuthCall('signInWithApple', () =>
        FirebaseAuthentication.signInWithApple({
          skipNativeAuth: false,
          scopes: ['email', 'name'],
        }),
      )

      const nativeCredential = nativeResult?.credential || null
      const idToken = nativeCredential?.idToken
      const rawNonce = nativeCredential?.nonce

      let signedInUser: User | null = null

      if (idToken) {
        try {
          const provider = new OAuthProvider('apple.com')
          const credential = provider.credential({
            idToken,
            rawNonce: rawNonce || undefined,
          })
          const result = await signInWithCredential(auth, credential)
          signedInUser = result.user
        } catch (credErr) {
          console.warn('[VOXNELA_AUTH] Apple credential with nonce failed, trying without nonce:', credErr)
          if (rawNonce) {
            try {
              const provider = new OAuthProvider('apple.com')
              const credential = provider.credential({ idToken })
              const result = await signInWithCredential(auth, credential)
              signedInUser = result.user
            } catch (retryErr) {
              console.warn('[VOXNELA_AUTH] Apple credential retry failed:', retryErr)
            }
          }
        }
      }

      if (!signedInUser) {
        signedInUser = await syncNativeAuthToWeb(nativeResult)
      }

      if (signedInUser) {
        void recordLegalAcceptance(signedInUser)
      } else {
        throw new Error('Apple sign-in could not create a web session.')
      }
    } catch (error: unknown) {
      console.error('[VOXNELA_AUTH] Apple login error:', error)
      // Fallback: try web popup if online
      try {
        if (navigator.onLine) {
          console.log('[VOXNELA_AUTH] Falling back to Web Apple popup...')
          const res = await signInWithPopup(auth, appleProvider)
          void recordLegalAcceptance(res.user)
          return
        }
      } catch (fallbackError) {
        console.warn('[VOXNELA_AUTH] Web Apple popup fallback failed:', fallbackError)
      }
      throw error
    }
  }

  const requestEmailCode = async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase()
    const language = document.documentElement.lang || navigator.language || 'tr'

    let dispatched = false
    try {
      await postOtpHttp('requestEmailLoginCodeHttp', {
        email: normalizedEmail,
        language,
      })
      dispatched = true
    } catch (httpError) {
      console.warn('[VOXNELA_AUTH] requestEmailLoginCodeHttp failed, falling back to callable:', httpError)
    }

    if (!dispatched) {
      const requestCode = httpsCallable<
        { email: string; language: string },
        { success: boolean }
      >(functions, 'requestEmailLoginCode')
      await requestCode({
        email: normalizedEmail,
        language,
      })
    }
  }

  const verifyEmailCodeSession = async (email: string, code: string) => {
    const normalizedEmail = email.trim().toLowerCase()
    const normalizedCode = code.replace(/\D/g, '').slice(0, 6)
    const language = document.documentElement.lang || navigator.language || 'tr'

    let customToken: string | undefined

    try {
      const data = await postOtpHttp<{ customToken?: string; success?: boolean }>(
        'verifyEmailLoginCodeHttp',
        {
          email: normalizedEmail,
          code: normalizedCode,
          language,
        },
      )
      if (data?.customToken) {
        customToken = data.customToken
      }
    } catch (httpError) {
      console.warn('[VOXNELA_AUTH] verifyEmailLoginCodeHttp failed, falling back to callable:', httpError)
    }

    if (!customToken) {
      const verifyCode = httpsCallable<
        { email: string; code: string; language: string },
        { success: boolean; customToken?: string; isNewUser?: boolean }
      >(functions, 'verifyEmailLoginCode')
      const result = await verifyCode({
        email: normalizedEmail,
        code: normalizedCode,
        language,
      })
      customToken = result.data.customToken
    }

    if (!customToken) {
      throw new Error('Güvenli giriş anahtarı alınamadı. Lütfen tekrar deneyin.')
    }

    const signedIn = await signInWithCustomToken(auth, customToken)
    void recordLegalAcceptance(signedIn.user)
  }

  const signInGoogle = () => withGuestHandoff(signInGoogleSession)
  const signInApple = () => withGuestHandoff(signInAppleSession)
  const verifyEmailCode = (email: string, code: string) =>
    withGuestHandoff(() => verifyEmailCodeSession(email, code))

  const logout = async () => {
    await signOutUser()
    // The app stays usable without an account after signing out.
    await ensureGuestSession()
  }

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isGuest,
      ensureGuestSession,
      signInGoogle,
      signInApple,
      requestEmailCode,
      verifyEmailCode,
      logout,
    }),
    [isGuest, loading, user],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
